import { RatingChip, useTechRatings } from '../../../components/admin/feedback/RatingBits';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Plus, Loader2, Trash2, Pencil, UserCog, X, Key, CalendarCheck, Clock, Save, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import BackfillWindowCard from '../../../components/admin/staff/BackfillWindowCard';

const EMPTY = {
  name: '', email: '', phone: '', role: 'Technician', level: 'junior',
  base_salary: 0, incentive_per_vehicle: 500,
  shift_start: '09:00', shift_end: '18:00',
  joining_date: todayISO(), status: 'active', initial_password: '',
  selfie_url: '', aadhar_url: '', aadhar_back_url: '',
  pan_url: '', pan_back_url: '',
  aadhar_number: '', pan_number: '',
  reporting_manager_id: '', reporting_manager_name: '',
  age: null, residence_address: '', id_proof_on_file: false, id_proof_type: '',
  languages_spoken: '', languages_read_write: '', prior_experience: '',
};

const STATUS_BADGES = {
  present: 'bg-emerald-100 text-emerald-700',
  half_day: 'bg-amber-100 text-amber-700',
  paid_leave: 'bg-sky-100 text-sky-700',
  unpaid_leave: 'bg-slate-200 text-slate-700',
  absent: 'bg-red-100 text-red-700',
};

const StaffPage = () => {
  const ratings = useTechRatings();
  const { authHeader, user: me } = useAuth();
  const isMaster = !!me?.is_master_admin;
  const [focusStaffId, setFocusStaffId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('focus') || '';
    } catch { return ''; }
  });
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null); // {id, password}
  const [attendanceMonth, setAttendanceMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [attendance, setAttendance] = useState({}); // {staff_id: [rows]}
  const [overtime, setOvertime] = useState({}); // {staff_id: { 'YYYY-MM-DD': {id, hours, notes} }}
  const [attendanceDraft, setAttendanceDraft] = useState({}); // {`${sid}|${date}`: status}
  const [otDraft, setOtDraft] = useState({}); // {`${sid}|${date}`: string}
  const [otDraftReasons, setOtDraftReasons] = useState({}); // {`${sid}|${date}`: 'reason'}
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalRows, setReasonModalRows] = useState([]); // [{key, staffName, date, hours}]
  const [otReasonPresets, setOtReasonPresets] = useState([]);
  const [presetsEditOpen, setPresetsEditOpen] = useState(false);
  const [presetsDraft, setPresetsDraft] = useState([]);
  const [presetsSaving, setPresetsSaving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [managerOptions, setManagerOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff`, { headers: authHeader() });
      setList(data || []);
      axios.get(`${API}/performance/manager-options`, { headers: authHeader() })
        .then(({ data: mo }) => setManagerOptions(mo?.options || []))
        .catch(() => setManagerOptions([]));
      // Pre-fetch attendance + overtime for everyone for the selected month
      const [y, m] = attendanceMonth.split('-');
      const lastDay = new Date(Number(y), Number(m), 0).getDate();
      const dfrom = `${attendanceMonth}-01`;
      const dto = `${attendanceMonth}-${String(lastDay).padStart(2, '0')}`;
      const rowsByStaff = {};
      const otByStaff = {};
      await Promise.all((data || []).map(async (s) => {
        const [{ data: a }, { data: o }] = await Promise.all([
          axios.get(
            `${API}/attendance?staff_id=${s.id}&date_from=${dfrom}&date_to=${dto}`,
            { headers: authHeader() },
          ),
          axios.get(
            `${API}/overtime?staff_id=${s.id}&date_from=${dfrom}&date_to=${dto}`,
            { headers: authHeader() },
          ),
        ]);
        rowsByStaff[s.id] = a || [];
        const otMap = {};
        (o || []).forEach((r) => {
          // If multiple entries exist for the same date (legacy from staff portal),
          // sum the hours so the admin grid still reflects reality.
          if (otMap[r.date]) {
            otMap[r.date] = {
              id: otMap[r.date].id,
              hours: (Number(otMap[r.date].hours) || 0) + (Number(r.hours) || 0),
              notes: otMap[r.date].notes || r.notes || '',
            };
          } else {
            otMap[r.date] = { id: r.id, hours: Number(r.hours) || 0, notes: r.notes || '' };
          }
        });
        otByStaff[s.id] = otMap;
      }));
      setAttendance(rowsByStaff);
      setOvertime(otByStaff);
      setOtDraft({});
      setOtDraftReasons({});
      setAttendanceDraft({});
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, attendanceMonth]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch OT reason presets once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/payroll/settings`, { headers: authHeader() });
        if (alive) setOtReasonPresets(Array.isArray(data?.ot_reason_presets) ? data.ot_reason_presets : []);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [authHeader]);

  const savePresets = async () => {
    setPresetsSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/payroll/settings`,
        { ot_reason_presets: presetsDraft },
        { headers: authHeader() },
      );
      setOtReasonPresets(Array.isArray(data?.ot_reason_presets) ? data.ot_reason_presets : []);
      setPresetsEditOpen(false);
    } catch (e) { setErr(formatErr(e)); }
    finally { setPresetsSaving(false); }
  };

  const appendPresetToReason = (key, text) => {
    setOtDraftReasons((prev) => {
      const cur = (prev[key] || '').trim();
      const next = cur ? `${cur}; ${text}` : text;
      return { ...prev, [key]: next };
    });
  };

  const daysInMonth = useMemo(() => {
    const [y, m] = attendanceMonth.split('-');
    return new Date(Number(y), Number(m), 0).getDate();
  }, [attendanceMonth]);

  const save = async () => {
    if (!editing.name || !editing.email) {
      setErr('Name and email required.');
      return;
    }
    if (!editing.id && (!editing.initial_password || editing.initial_password.length < 8)) {
      setErr('Initial password required (min 8 characters).');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (editing.id) {
        const { initial_password: _unused, id: _idIgnored, user_id: _uidIgnored, created_at: _cat, updated_at: _uat, ...payload } = editing;
        await axios.put(`${API}/staff/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/staff`, editing, { headers: authHeader() });
      }
      setEditing(null);
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This also deletes their login account. Attendance + payroll history is preserved.`)) return;
    try {
      await axios.delete(`${API}/staff/${s.id}`, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
  };

  const doReset = async () => {
    if (!resetting.password || resetting.password.length < 8) {
      alert('Password must be at least 8 characters.');
      return;
    }
    try {
      await axios.put(`${API}/staff/${resetting.id}`, { reset_password: resetting.password }, { headers: authHeader() });
      setResetting(null);
      alert('Password reset. Share the new password with the staff member.');
    } catch (e) { notifyErr(e); }
  };

  const attendanceStatusFor = (sid, day) => {
    const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
    const key = `${sid}|${date}`;
    if (Object.prototype.hasOwnProperty.call(attendanceDraft, key)) {
      return { status: attendanceDraft[key], isDraft: true };
    }
    const row = (attendance[sid] || []).find((r) => r.date === date);
    return { status: row?.status || '', isDraft: false };
  };

  const cellColor = (sid, day) => {
    const { status } = attendanceStatusFor(sid, day);
    if (!status) return 'bg-slate-50 text-slate-300';
    return `${STATUS_BADGES[status] || 'bg-slate-100'} cursor-default`;
  };

  const cellLabel = (sid, day) => {
    const { status } = attendanceStatusFor(sid, day);
    if (!status) return '·';
    return ({ present: 'P', half_day: 'H', paid_leave: 'PL', unpaid_leave: 'UL', absent: 'A' })[status] || '·';
  };

  const cycleAttendance = (sid, day) => {
    const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
    const key = `${sid}|${date}`;
    const { status: cur } = attendanceStatusFor(sid, day);
    const next = cur === 'present' ? 'half_day'
      : cur === 'half_day' ? 'paid_leave'
        : cur === 'paid_leave' ? 'unpaid_leave'
          : cur === 'unpaid_leave' ? 'absent'
            : 'present';
    // Look up the stored value (no draft) to detect "draft equals stored" → drop draft
    const stored = (attendance[sid] || []).find((r) => r.date === date)?.status || '';
    setAttendanceDraft((p) => {
      const n = { ...p };
      if (next === stored) delete n[key];
      else n[key] = next;
      return n;
    });
  };

  const otValueFor = (sid, day) => {
    const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
    const key = `${sid}|${date}`;
    const stored = (overtime[sid] || {})[date]?.hours;
    const draftHas = Object.prototype.hasOwnProperty.call(otDraft, key);
    return {
      display: draftHas ? otDraft[key] : (stored && stored > 0 ? String(stored) : ''),
      stored: Number(stored || 0),
      isDraft: draftHas && otDraft[key] !== (stored && stored > 0 ? String(stored) : ''),
    };
  };

  const setOtDraftValue = (sid, day, rawValue) => {
    const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
    const key = `${sid}|${date}`;
    const storedRaw = (overtime[sid] || {})[date]?.hours;
    const storedStr = storedRaw && Number(storedRaw) > 0 ? String(storedRaw) : '';
    setOtDraft((p) => {
      const n = { ...p };
      // Normalise — if user types something equal to stored, drop the draft
      if (String(rawValue ?? '') === storedStr) delete n[key];
      else n[key] = rawValue;
      return n;
    });
  };

  const otMonthlyTotal = (sid) => {
    const map = overtime[sid] || {};
    // Start from stored
    const dates = new Set(Object.keys(map));
    // Add any draft-only dates
    Object.keys(otDraft).forEach((key) => {
      const [s, d] = key.split('|');
      if (s === sid) dates.add(d);
    });
    let total = 0;
    dates.forEach((d) => {
      const key = `${sid}|${d}`;
      if (Object.prototype.hasOwnProperty.call(otDraft, key)) {
        total += Math.max(0, Number(otDraft[key] || 0));
      } else {
        total += Number((map[d] || {}).hours || 0);
      }
    });
    return total;
  };

  const pendingCount = Object.keys(attendanceDraft).length + Object.keys(otDraft).length;

  const discardDrafts = () => {
    if (pendingCount > 0 && !window.confirm(`Discard ${pendingCount} unsaved change(s)?`)) return;
    setAttendanceDraft({});
    setOtDraft({});
    setOtDraftReasons({});
  };

  // List of NEW non-zero OT draft cells that don't yet have a reason captured
  // (existing reasons from the previously stored doc are reused via fallback).
  const otDraftsNeedingReason = () => {
    const out = [];
    Object.entries(otDraft).forEach(([key, raw]) => {
      const hours = Math.max(0, Number(raw || 0));
      if (hours <= 0) return;  // clearing → no reason needed
      if ((otDraftReasons[key] || '').trim()) return;  // user already typed one
      const [sid, date] = key.split('|');
      const staff = list.find((x) => x.id === sid);
      const existingNote = ((overtime[sid] || {})[date] || {}).notes || '';
      out.push({
        key, sid, date, hours,
        staffName: staff?.name || sid,
        existingNote,
      });
    });
    return out;
  };

  const requestSave = () => {
    if (pendingCount === 0) return;
    const needs = otDraftsNeedingReason();
    if (needs.length === 0) {
      saveAllDrafts();
      return;
    }
    // Pre-fill draft reasons with any existing notes for those dates
    setOtDraftReasons((prev) => {
      const next = { ...prev };
      needs.forEach((n) => {
        if (n.existingNote && !next[n.key]) next[n.key] = n.existingNote;
      });
      return next;
    });
    setReasonModalRows(needs);
    setReasonModalOpen(true);
  };

  const confirmReasonsAndSave = () => {
    // Validate every row has a non-empty reason
    const blank = reasonModalRows.filter((r) => !(otDraftReasons[r.key] || '').trim());
    if (blank.length > 0) {
      setErr(`Please add a reason for all ${blank.length} OT entr${blank.length === 1 ? 'y' : 'ies'}.`);
      return;
    }
    setReasonModalOpen(false);
    saveAllDrafts();
  };

  const saveAllDrafts = async () => {
    if (pendingCount === 0) return;
    setSavingAll(true); setErr('');
    try {
      const tasks = [];
      Object.entries(attendanceDraft).forEach(([key, status]) => {
        const [sid, date] = key.split('|');
        tasks.push(
          axios.post(`${API}/attendance/mark?staff_id=${sid}`, { date, status }, { headers: authHeader() }),
        );
      });
      Object.entries(otDraft).forEach(([key, raw]) => {
        const [sid, date] = key.split('|');
        const hours = Math.max(0, Number(raw || 0));
        // Reason: prefer draft reason, fall back to existing stored note on the row
        const existingNote = ((overtime[sid] || {})[date] || {}).notes || '';
        const notes = (otDraftReasons[key] || '').trim() || existingNote;
        tasks.push(
          axios.post(
            `${API}/staff/${sid}/overtime/set-day`,
            { date, hours, invoice_ids: [], notes },
            { headers: authHeader() },
          ),
        );
      });
      const results = await Promise.allSettled(tasks);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        setErr(`${failed.length} of ${tasks.length} change(s) failed to save. Please retry.`);
      }
      await fetchAll();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="staff-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserCog className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Staff Master</h1>
        </div>
        {isMaster ? (
          <button onClick={() => { setErr(''); setEditing({ ...EMPTY }); }} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-staff-btn">
            <Plus size={14} /> Add Staff
          </button>
        ) : (
          <span className="text-xs bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-3 py-1.5" data-testid="staff-readonly-badge">
            Read-only · Master admin can add / edit staff
          </span>
        )}
      </div>

      {/* Backfill window — master admin only */}
      <BackfillWindowCard />

      {/* Pending changes bar — sticky just below the page header */}
      {pendingCount > 0 && (
        <div
          className="sticky top-0 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-amber-100/90 backdrop-blur border-y border-amber-300 flex flex-wrap items-center justify-between gap-3"
          data-testid="pending-changes-bar"
        >
          <div className="text-sm text-amber-900 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white font-bold text-xs" data-testid="pending-count">{pendingCount}</span>
            <span><b>Unsaved change(s)</b> in attendance & OT — click Save to commit.</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={discardDrafts}
              disabled={savingAll}
              className="text-sm bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="discard-changes-btn"
            >
              <RotateCcw size={14} /> Discard
            </button>
            <button
              onClick={requestSave}
              disabled={savingAll}
              className="text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
              data-testid="save-changes-btn"
            >
              {savingAll ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              {savingAll ? 'Saving…' : `Save ${pendingCount} change${pendingCount > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Master list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No staff yet. Add your first technician above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold text-slate-600">Name</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Role</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Level</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Base / mo</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Per-vehicle</th>
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">OT/hr (calc)</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((s) => {
                  const isFocused = focusStaffId && s.id === focusStaffId;
                  return (
                  <tr
                    key={s.id}
                    className={isFocused ? 'bg-amber-50 ring-2 ring-amber-300' : 'hover:bg-slate-50'}
                    ref={(el) => {
                      if (el && isFocused) {
                        setTimeout(() => {
                          try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
                          setTimeout(() => setFocusStaffId(''), 5000);
                        }, 200);
                      }
                    }}
                    data-testid={`staff-row-${s.id}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{s.name}</div>
                      <div className="text-[11px] text-slate-500">{s.email}</div>
                      <RatingChip t={ratings[s.id]} testid={`staff-rating-${s.id}`} />
                    </td>
                    <td className="px-3 py-2 text-slate-700">{s.role}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.level === 'senior' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'}`}>{s.level}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtINR(s.base_salary)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtINR(s.incentive_per_vehicle)}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-500">
                      {s.base_salary > 0 ? `₹${((s.base_salary / 26) / 9 * 2).toFixed(0)}` : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{s.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isMaster ? (
                        <div className="inline-flex gap-1">
                          <button onClick={() => setResetting({ id: s.id, password: '' })} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Reset password" data-testid={`reset-${s.id}`}><Key size={14} /></button>
                          <button onClick={() => { setErr(''); setEditing({ ...s }); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-staff-${s.id}`}><Pencil size={14} /></button>
                          <button onClick={() => del(s)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`del-staff-${s.id}`}><Trash2 size={14} /></button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">view only</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attendance grid */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <CalendarCheck size={16} className="text-slate-700" />
            <h2 className="text-sm font-semibold">Attendance — {attendanceMonth}</h2>
          </div>
          <input type="month" value={attendanceMonth} onChange={(e) => setAttendanceMonth(e.target.value)} className={inputCls} data-testid="attendance-month" />
        </div>
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-600 z-10">Staff</th>
                {Array.from({ length: daysInMonth }, (_, i) => (
                  <th key={i + 1} className="px-1 py-2 text-center font-semibold text-slate-600 min-w-[42px] text-sm">{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 font-medium text-slate-700 whitespace-nowrap z-10">{s.name}</td>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
                    const key = `${s.id}|${date}`;
                    const isDraft = Object.prototype.hasOwnProperty.call(attendanceDraft, key);
                    return (
                      <td key={day} className="p-1">
                        <button
                          onClick={() => cycleAttendance(s.id, day)}
                          className={`w-10 h-10 rounded-lg text-sm font-bold ${cellColor(s.id, day)} ${isDraft ? 'ring-2 ring-amber-500 ring-offset-1' : ''}`}
                          title={isDraft ? 'Unsaved — click Save to commit' : 'Click to cycle status'}
                          data-testid={`att-${s.id}-${day}`}
                        >
                          {cellLabel(s.id, day)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11px] text-slate-500 flex flex-wrap gap-3 border-t border-slate-100">
          <span><span className="inline-block w-3 h-3 align-middle bg-emerald-100 rounded mr-1" /> P = Present</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-amber-100 rounded mr-1" /> H = Half Day</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-sky-100 rounded mr-1" /> PL = Paid Leave</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-slate-200 rounded mr-1" /> UL = Unpaid Leave</span>
          <span><span className="inline-block w-3 h-3 align-middle bg-red-100 rounded mr-1" /> A = Absent</span>
          <span className="ml-auto">Click a cell to cycle status. Changes are draft until you click <b>Save</b>.</span>
        </div>
      </div>

      {/* Overtime grid — admin daily OT entry */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ot-grid-card">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-amber-50/60">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-amber-700" />
            <h2 className="text-sm font-semibold">Overtime hours — {attendanceMonth}</h2>
          </div>
          <span className="text-[11px] text-slate-500">
            Type hours into a cell (e.g. <span className="font-mono">1.5</span>). Changes are draft until you click <b>Save</b>. Leave blank or 0 to clear.
          </span>
          <button
            type="button"
            onClick={() => { setPresetsDraft([...otReasonPresets]); setPresetsEditOpen(true); }}
            className="text-[11px] text-amber-700 hover:text-amber-900 underline ml-auto"
            data-testid="ot-card-manage-presets-btn"
          >
            Manage preset reasons
          </button>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">Add staff above to record overtime.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-slate-50 px-3 py-2 text-left font-semibold text-slate-600 z-10">Staff</th>
                  {Array.from({ length: daysInMonth }, (_, i) => (
                    <th key={i + 1} className="px-1 py-2 text-center font-semibold text-slate-600 min-w-[52px] text-sm">{i + 1}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-semibold text-slate-600 bg-amber-50 sticky right-0 z-10">Total</th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="sticky left-0 bg-white px-3 py-2 font-medium text-slate-700 whitespace-nowrap z-10">{s.name}</td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const date = `${attendanceMonth}-${String(day).padStart(2, '0')}`;
                      const { display, stored, isDraft } = otValueFor(s.id, day);
                      const storedNote = ((overtime[s.id] || {})[date] || {}).notes || '';
                      const titleParts = [];
                      if (stored > 0) titleParts.push(`${stored}h logged`);
                      if (storedNote) titleParts.push(`Reason: ${storedNote}`);
                      const title = titleParts.length ? titleParts.join(' · ') : 'Enter OT hours; reason will be requested on Save';
                      return (
                        <td key={day} className="p-1">
                          <div className="relative" title={title}>
                            <input
                              type="number"
                              min="0"
                              step="0.25"
                              value={display}
                              placeholder="—"
                              onChange={(e) => setOtDraftValue(s.id, day, e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              className={`w-12 h-10 text-center rounded-lg border text-sm font-mono outline-none transition
                                ${isDraft
                                  ? 'border-amber-500 ring-2 ring-amber-300 bg-white text-amber-900'
                                  : stored > 0
                                    ? 'border-amber-300 bg-amber-50 text-amber-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200'
                                    : 'border-slate-200 bg-slate-50 text-slate-500 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 focus:bg-white'}`}
                              data-testid={`ot-${s.id}-${day}`}
                              disabled={savingAll}
                            />
                            {storedNote && !isDraft && (
                              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono font-semibold text-amber-700 bg-amber-50 sticky right-0 z-10" data-testid={`ot-total-${s.id}`}>
                      {otMonthlyTotal(s.id).toFixed(2)} h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2 text-[11px] text-slate-500 border-t border-slate-100 flex flex-wrap gap-3">
          <span>OT rate is auto-derived per staff: <span className="font-mono">(base ÷ 26 ÷ 9) × 2</span>.</span>
          <span className="ml-auto">OT hours roll into the monthly payroll automatically.</span>
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">{editing.id ? 'Edit Staff' : 'Add Staff'}</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{err}</div>}
              <div className="grid grid-cols-2 gap-3">
                <input type="text" placeholder="Name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={inputCls} data-testid="staff-name" />
                <input type="email" placeholder="Email *" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} data-testid="staff-email" />
                {editing.id && (
                  <p className="col-span-2 -mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠ Changing the email updates both this staff record AND their login. They will need to use the new email to log in.
                  </p>
                )}
                <input type="text" placeholder="Phone" value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={inputCls} data-testid="staff-phone" />
                <input type="text" placeholder="Role / Title" value={editing.role || ''} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className={inputCls} data-testid="staff-role" />
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Level</label>
                  <select value={editing.level} onChange={(e) => setEditing({ ...editing, level: e.target.value })} className={inputCls} data-testid="staff-level">
                    <option value="junior">Junior</option>
                    <option value="senior">Senior</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Status</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inputCls} data-testid="staff-status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Base salary / month (₹)</label>
                  <input type="number" min={0} value={editing.base_salary} onChange={(e) => setEditing({ ...editing, base_salary: Number(e.target.value || 0) })} className={inputCls} data-testid="staff-base-salary" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Incentive per vehicle (₹)</label>
                  <input type="number" min={0} value={editing.incentive_per_vehicle} onChange={(e) => setEditing({ ...editing, incentive_per_vehicle: Number(e.target.value || 0) })} className={inputCls} data-testid="staff-incentive" />
                </div>
                <div className="col-span-2 bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                  <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Computed OT rate</div>
                  <div className="font-mono text-slate-900">
                    {(() => {
                      const b = Number(editing.base_salary || 0);
                      if (b <= 0) return '—  (set base salary)';
                      const daily = b / 26;
                      const hourly = daily / 9;
                      const ot = hourly * 2;
                      return `₹${ot.toFixed(2)}/hr   ·   (base ${b.toFixed(0)} ÷ 26 ÷ 9 × 2)`;
                    })()}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Joining date</label>
                  <input type="date" value={editing.joining_date} onChange={(e) => setEditing({ ...editing, joining_date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Shift start</label>
                  <input type="time" value={editing.shift_start || '09:00'} onChange={(e) => setEditing({ ...editing, shift_start: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Shift end</label>
                  <input type="time" value={editing.shift_end || '18:00'} onChange={(e) => setEditing({ ...editing, shift_end: e.target.value })} className={inputCls} />
                </div>
                <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                    Skill-sheet profile
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Age</label>
                      <input
                        type="number" value={editing.age ?? ''}
                        onChange={(e) => setEditing({ ...editing, age: e.target.value === '' ? null : Number(e.target.value) })}
                        className={inputCls} data-testid="staff-age"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">ID proof</label>
                      <div className="flex gap-2">
                        <select
                          value={editing.id_proof_on_file ? 'yes' : 'no'}
                          onChange={(e) => setEditing({ ...editing, id_proof_on_file: e.target.value === 'yes' })}
                          className={`${inputCls} !w-24`} data-testid="staff-id-proof-on-file"
                        >
                          <option value="no">Not on file</option>
                          <option value="yes">On file</option>
                        </select>
                        <input
                          placeholder="Type (Aadhaar / PAN / DL)"
                          value={editing.id_proof_type || ''}
                          onChange={(e) => setEditing({ ...editing, id_proof_type: e.target.value })}
                          className={inputCls} data-testid="staff-id-proof-type"
                        />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-slate-500 mb-1">Residence address</label>
                      <input
                        value={editing.residence_address || ''}
                        onChange={(e) => setEditing({ ...editing, residence_address: e.target.value })}
                        className={inputCls} data-testid="staff-residence"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Languages — speak</label>
                      <input
                        value={editing.languages_spoken || ''}
                        onChange={(e) => setEditing({ ...editing, languages_spoken: e.target.value })}
                        className={inputCls} data-testid="staff-languages-spoken"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Languages — read &amp; write</label>
                      <input
                        value={editing.languages_read_write || ''}
                        onChange={(e) => setEditing({ ...editing, languages_read_write: e.target.value })}
                        className={inputCls} data-testid="staff-languages-rw"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-slate-500 mb-1">Prior experience / trainee background</label>
                      <input
                        placeholder="e.g. Polish & ceramic 2 years, sunfilm 6 months"
                        value={editing.prior_experience || ''}
                        onChange={(e) => setEditing({ ...editing, prior_experience: e.target.value })}
                        className={inputCls} data-testid="staff-prior-experience"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">
                    Reporting manager <span className="text-slate-400 font-normal">(evaluates them in the Performance Management System)</span>
                  </label>
                  <select
                    value={editing.reporting_manager_id || ''}
                    onChange={(e) => {
                      const opt = managerOptions.find((o) => o.id === e.target.value);
                      setEditing({
                        ...editing,
                        reporting_manager_id: e.target.value,
                        reporting_manager_name: opt?.name || '',
                      });
                    }}
                    className={inputCls}
                    data-testid="staff-reporting-manager"
                  >
                    <option value="">— Not assigned —</option>
                    {managerOptions
                      .filter((o) => o.id !== editing.id)
                      .map((o) => <option key={o.id} value={o.id}>{o.name} · {o.sub}</option>)}
                  </select>
                </div>
                {!editing.id && (
                  <div className="col-span-2">
                    <label className="block text-xs text-slate-500 mb-1">Initial password * <span className="text-slate-400 font-normal">(share with staff for first login — min 8 chars)</span></label>
                    <input type="text" value={editing.initial_password || ''} onChange={(e) => setEditing({ ...editing, initial_password: e.target.value })} className={inputCls} data-testid="staff-initial-pw" />
                  </div>
                )}
                <div className="col-span-2 mt-3 pt-3 border-t border-slate-200">
                  <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Identity Documents (PII — visible to admin only)</h3>
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
                    {[
                      { key: 'selfie_url',      label: 'Selfie photo' },
                      { key: 'aadhar_url',      label: 'Aadhar — front' },
                      { key: 'aadhar_back_url', label: 'Aadhar — back' },
                      { key: 'pan_url',         label: 'PAN — front' },
                      { key: 'pan_back_url',    label: 'PAN — back' },
                    ].map((doc) => (
                      <div key={doc.key} className="border border-slate-200 rounded-lg p-2 bg-slate-50">
                        <div className="text-[11px] text-slate-600 mb-1">{doc.label}</div>
                        {editing[doc.key] ? (
                          <>
                            <a href={editing[doc.key]} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded overflow-hidden mb-1">
                              <img src={editing[doc.key]} alt={doc.label} className="w-full h-full object-cover" />
                            </a>
                            <button onClick={() => setEditing({ ...editing, [doc.key]: '' })} className="text-[11px] text-red-600 hover:underline">Remove</button>
                          </>
                        ) : (
                          <label className="cursor-pointer block w-full">
                            <div className="aspect-square bg-white border border-dashed border-slate-300 rounded flex items-center justify-center text-[11px] text-slate-400 hover:bg-slate-100 mb-1">
                              + Upload
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              data-testid={`upload-${doc.key}`}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                try {
                                  // `doc.key` is e.g. "selfie_url" — strip the "_url"
                                  // suffix to get the backend-expected kind value.
                                  const kind = doc.key.replace(/_url$/, '');
                                  const fd = new FormData();
                                  fd.append('file', file);
                                  fd.append('kind', kind);
                                  const { data } = await axios.post(
                                    `${API}/staff/upload-doc`,
                                    fd,
                                    { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
                                  );
                                  const url = data?.secure_url;
                                  if (url) {
                                    setEditing((p) => ({ ...p, [doc.key]: url }));
                                  } else {
                                    alert('Upload succeeded but no URL returned by the server.');
                                  }
                                } catch (ex) {
                                  notifyErr(ex);
                                } finally {
                                  // Reset the input so re-selecting the same file fires onChange again.
                                  e.target.value = '';
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <input type="text" placeholder="Aadhar number" value={editing.aadhar_number || ''} onChange={(e) => setEditing({ ...editing, aadhar_number: e.target.value })} className={inputCls} data-testid="staff-aadhar-no" />
                    <input type="text" placeholder="PAN number" value={editing.pan_number || ''} onChange={(e) => setEditing({ ...editing, pan_number: e.target.value })} className={inputCls} data-testid="staff-pan-no" />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg" data-testid="staff-save">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">Reset Password</h2>
              <button onClick={() => setResetting(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-slate-500">Set a new password for this staff. They'll need to use it on next login.</p>
              <input type="text" placeholder="New password (min 8 chars)" value={resetting.password} onChange={(e) => setResetting({ ...resetting, password: e.target.value })} className={inputCls} data-testid="reset-pw-input" />
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setResetting(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={doReset} className="text-sm bg-amber-500 hover:bg-amber-600 text-white font-semibold px-3 py-2 rounded-lg" data-testid="reset-pw-confirm">Reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Reason capture modal — prompts user for a reason on every new non-zero OT draft before save */}      {reasonModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="ot-reason-modal">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-amber-500 to-amber-600 text-white">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider opacity-90">Add reason for overtime</h2>
                <p className="text-sm">Each new OT entry needs a reason for audit trail.</p>
              </div>
              <button onClick={() => setReasonModalOpen(false)} className="p-1.5 rounded hover:bg-white/20" data-testid="ot-reason-close-btn"><X size={18} /></button>
            </div>

            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{err}</div>}
              {reasonModalRows.map((r) => (
                <div key={r.key} className="border border-slate-200 rounded-lg p-3" data-testid={`ot-reason-row-${r.key}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-semibold text-slate-800 text-sm">{r.staffName}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.date} · <span className="text-amber-700 font-bold">{r.hours} h</span></div>
                  </div>
                  {otReasonPresets.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`ot-reason-presets-${r.key}`}>
                      {otReasonPresets.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => appendPresetToReason(r.key, p)}
                          className="text-[11px] bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-medium px-2 py-0.5 rounded-full transition"
                          data-testid={`ot-reason-chip-${r.key}-${p.replace(/[^a-z0-9]/gi, '-').slice(0, 20)}`}
                        >
                          + {p}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    rows={2}
                    value={otDraftReasons[r.key] || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOtDraftReasons((prev) => ({ ...prev, [r.key]: v }));
                    }}
                    placeholder="Click a chip above or type a reason (e.g. customer pickup delayed, urgent PPF, helped another job)"
                    className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                    data-testid={`ot-reason-input-${r.key}`}
                    autoFocus={r === reasonModalRows[0]}
                  />
                </div>
              ))}
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex flex-wrap justify-between items-center gap-2">
              <button
                type="button"
                onClick={() => { setPresetsDraft([...otReasonPresets]); setPresetsEditOpen(true); }}
                className="text-[11px] text-amber-700 hover:text-amber-900 underline"
                data-testid="ot-reason-manage-presets-btn"
              >
                Manage preset reasons →
              </button>
              <div className="flex gap-2">
                <button onClick={() => setReasonModalOpen(false)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200" data-testid="ot-reason-cancel-btn">Back</button>
                <button onClick={confirmReasonsAndSave} className="text-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="ot-reason-confirm-btn">
                  <Save size={14} />
                  Save all {pendingCount} change{pendingCount > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preset OT reasons editor */}
      {presetsEditOpen && (
        <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ot-presets-modal">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="text-sm font-semibold text-slate-800">Preset OT reasons</h3>
              <button onClick={() => setPresetsEditOpen(false)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
              <p className="text-[11px] text-slate-500 mb-2">
                Up to 20 presets. Empty rows are dropped on save. Case-insensitive duplicates removed automatically.
              </p>
              {presetsDraft.map((p, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`ot-preset-row-${idx}`}>
                  <input
                    type="text"
                    value={p}
                    onChange={(e) => {
                      const next = [...presetsDraft];
                      next[idx] = e.target.value;
                      setPresetsDraft(next);
                    }}
                    className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
                    placeholder={`Preset reason #${idx + 1}`}
                    data-testid={`ot-preset-input-${idx}`}
                  />
                  <button
                    onClick={() => setPresetsDraft(presetsDraft.filter((_, i) => i !== idx))}
                    className="p-1.5 rounded hover:bg-red-50 text-red-500"
                    title="Remove"
                    data-testid={`ot-preset-del-${idx}`}
                  ><Trash2 size={14} /></button>
                </div>
              ))}
              {presetsDraft.length < 20 && (
                <button
                  type="button"
                  onClick={() => setPresetsDraft([...presetsDraft, ''])}
                  className="text-xs bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 mt-2"
                  data-testid="ot-preset-add-btn"
                >
                  <Plus size={12} /> Add preset
                </button>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setPresetsEditOpen(false)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button
                onClick={savePresets}
                disabled={presetsSaving}
                className="text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
                data-testid="ot-presets-save-btn"
              >
                {presetsSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                {presetsSaving ? 'Saving…' : 'Save presets'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPage;
